import { useEffect, useState } from "react";
import { create } from "zustand";

// Eigendomsinformatie per projectperceel (GET .../ownership). De sleutels van
// `summary` zijn opgeslagen data en blijven Nederlands; KIK levert veldwaarden
// als tekst, de PDF-parser als lijst van regels.
export type OwnershipRight = {
  nummer?: string;
  titel?: string;
  velden?: Record<string, string | string[]>;
};

export type OwnershipSummary = {
  kadastrale_aanduiding?: string;
  kadastrale_grootte_m2?: number;
  koopsom_eur?: number;
  koopjaar?: number;
  rechten?: OwnershipRight[];
  aantekeningen?: { aard: string; omschrijving: string }[];
  overgegaan_in?: string[];
  // Cultuur (bebouwd/onbebouwd), zoals "Wonen" of "Erf - tuin".
  omschrijving?: string[];
  // Gesplitst perceel: geen rechten op de grond, wel appartementsrechten en een VvE.
  splitsing?: {
    type?: string;
    vve?: string;
    vve_zetel?: string;
    appartementsrechten?: string[];
  };
};

export type Ownership = {
  parcelId: number;
  parcelName: string;
  source: "kik-inzage" | "pdf";
  deliveredAt?: string | null;
  currentAsOf?: string | null;
  fileUrl?: string | null;
  summary: OwnershipSummary;
};

/** De eigendomsinformatie achter `url`; undefined zolang er niets is (nog
 * niet geladen, geen url, of een fout: dan toont het rapport niets). */
export const useOwnership = (url?: string) => {
  const [rows, setRows] = useState<Ownership[] | undefined>();
  const revision = useOwnershipRevision((s) => s.revision);
  // Wiens rijen er staan: bij een ander project of perceel meteen leeg, bij
  // alleen een nieuwe revisie blijven de oude staan tot de nieuwe er zijn.
  const [rowsFor, setRowsFor] = useState(url);
  if (rowsFor !== url) {
    setRowsFor(url);
    setRows(undefined);
  }
  useEffect(() => {
    if (!url) return;
    // Afbreken in plaats van alleen negeren: een verouderd verzoek (ander
    // perceel, nieuwe revisie) hoeft niet af te lopen.
    const controller = new AbortController();
    fetch(url, { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : undefined))
      .then((data) => setRows(data))
      .catch(() => {
        if (!controller.signal.aborted) setRows(undefined);
      });
    return () => controller.abort();
  }, [url, revision]);
  return rows;
};

/**
 * Telt de nieuwe leveringen: ProjectFiles hoogt hem op na "Eigendomsinfo
 * opvragen", en elk open rapport met `useOwnership` haalt dan opnieuw op, in
 * plaats van pas na opnieuw openen.
 */
export const useOwnershipRevision = create<{ revision: number; bump: () => void }>((set) => ({
  revision: 0,
  bump: () => set((s) => ({ revision: s.revision + 1 })),
}));
